import {Link} from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Package,
  RefreshCw,
  Send,
  SkipForward,
  ImageOff
} from "lucide-react";
import type {AdminSymphonyaConnectionTest, AdminSymphonyaDashboard} from "@corporatesclub/shared";
import {Button} from "@/components/ui/button";
import {AdminAlert, AdminBadge, AdminCard, AdminToolbar} from "./ui";
import {cn} from "@/lib/utils";

type Props = {
  data: AdminSymphonyaDashboard | null;
  busy: "" | "products" | "orders" | "test";
  connectionTest?: AdminSymphonyaConnectionTest | null;
  onSyncProducts: () => void;
  onSendOrders: () => void;
  onTestConnection: () => void;
  onRefresh: () => void;
};

function jobLabel(type: string) {
  if(type === "sync-products") return "Sync products";
  if(type === "send-orders") return "Send orders (batch)";
  if(type === "send-order") return "Send order";
  return type;
}

function symTone(label: string): "warning" | "success" | "danger" | "secondary" | "outline" {
  if(label === "pending") return "warning";
  if(label === "synced") return "success";
  if(label === "error") return "danger";
  if(label === "skipped") return "secondary";
  return "outline";
}

export function SymphonyaDashboardPanel({data, busy, connectionTest, onSyncProducts, onSendOrders, onTestConnection, onRefresh}: Props) {
  const k = data?.kpis;
  return (
    <AdminCard
      title="Symphonya integrations"
      description="Order push status, catalog sync jobs, and recent activity."
      actions={
        <AdminToolbar className="mb-0">
          <Button type="button" variant="outline" size="sm" onClick={onRefresh} disabled={busy !== ""}>
            <RefreshCw className={cn("h-4 w-4", busy !== "" && "animate-spin")} /> Refresh
          </Button>
          <Button type="button" variant="default" size="sm" onClick={onSyncProducts} disabled={busy !== ""}>
            <RefreshCw className="h-4 w-4" /> {busy === "products" ? "Syncing…" : "Sync products"}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={onTestConnection} disabled={busy !== ""}>
            <AlertTriangle className="h-4 w-4" /> {busy === "test" ? "Testing…" : "Test connection"}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={onSendOrders} disabled={busy !== ""}>
            <Send className="h-4 w-4" /> {busy === "orders" ? "Sending…" : "Send orders"}
          </Button>
        </AdminToolbar>
      }
    >
      {!data || !k ? (
        <p className="text-sm text-muted-foreground">Loading Symphonya dashboard…</p>
      ) : (
        <div className="admin-sym">
          {connectionTest ? (
            <AdminAlert variant="info" title={`Server outbound IP: ${connectionTest.outboundIp ?? "unknown"}`}>
              <ul className="mt-2 space-y-2 text-xs">
                {Object.entries(connectionTest.tests).map(([name, t]) => (
                  <li key={name} className="rounded-lg border border-border/60 bg-background/80 p-2">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{name}</span>
                      <AdminBadge variant={t.ok ? "success" : t.httpCode === 403 ? "danger" : "warning"}>
                        HTTP {t.httpCode}
                      </AdminBadge>
                    </div>
                    <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-muted-foreground">{t.bodyPreview || "—"}</pre>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-muted-foreground">
                If createOrder shows HTTP 403 / Access denied, give the outbound IP above to Symphonya for whitelist.
              </p>
            </AdminAlert>
          ) : null}
          <div className="admin-sym__kpis">
            <div className="admin-sym-kpi admin-sym-kpi--amber">
              <Clock3 className="h-4 w-4" />
              <div>
                <p className="admin-sym-kpi__label">Pending push</p>
                <p className="admin-sym-kpi__value">{k.pending}</p>
                <p className="admin-sym-kpi__hint">{k.pendingToday} today</p>
              </div>
            </div>
            <div className="admin-sym-kpi admin-sym-kpi--emerald">
              <CheckCircle2 className="h-4 w-4" />
              <div>
                <p className="admin-sym-kpi__label">Synced</p>
                <p className="admin-sym-kpi__value">{k.synced}</p>
                <p className="admin-sym-kpi__hint">ERP id set</p>
              </div>
            </div>
            <div className="admin-sym-kpi admin-sym-kpi--rose">
              <AlertTriangle className="h-4 w-4" />
              <div>
                <p className="admin-sym-kpi__label">Errors</p>
                <p className="admin-sym-kpi__value">{k.errors}</p>
                <p className="admin-sym-kpi__hint">{k.errorsToday} today</p>
              </div>
            </div>
            <div className="admin-sym-kpi admin-sym-kpi--slate">
              <SkipForward className="h-4 w-4" />
              <div>
                <p className="admin-sym-kpi__label">Skipped</p>
                <p className="admin-sym-kpi__value">{k.skipped}</p>
                <p className="admin-sym-kpi__hint">_symphonya = -1</p>
              </div>
            </div>
            <div className="admin-sym-kpi admin-sym-kpi--sky">
              <Package className="h-4 w-4" />
              <div>
                <p className="admin-sym-kpi__label">Active products</p>
                <p className="admin-sym-kpi__value">{k.productsActive}</p>
                <p className="admin-sym-kpi__hint">activ = 0</p>
              </div>
            </div>
            <div className="admin-sym-kpi admin-sym-kpi--violet">
              <ImageOff className="h-4 w-4" />
              <div>
                <p className="admin-sym-kpi__label">Images pending</p>
                <p className="admin-sym-kpi__value">{k.productsImagesPending}</p>
                <p className="admin-sym-kpi__hint">catalog flag</p>
              </div>
            </div>
          </div>

          <div className="admin-sym__meta">
            <p>
              <span className="text-muted-foreground">Last product sync:</span>{" "}
              {data.lastProductSync
                ? <><strong>{data.lastProductSync.at}</strong> · {data.lastProductSync.status} · {data.lastProductSync.message}</>
                : <em>No job logged yet</em>}
            </p>
            <p>
              <span className="text-muted-foreground">Last order push:</span>{" "}
              {data.lastOrderPush
                ? <><strong>{data.lastOrderPush.at}</strong> · {data.lastOrderPush.status} · {data.lastOrderPush.message}</>
                : <em>No job logged yet</em>}
            </p>
          </div>

          <div className="admin-sym__cols">
            <section className="admin-sym-panel">
              <header className="admin-sym-panel__head">
                <h3>Job log</h3>
                <span>What ran, when</span>
              </header>
              {data.jobs.length === 0 ? (
                <p className="admin-sym-panel__empty">No sync jobs logged yet. Run Sync products or Send orders.</p>
              ) : (
                <ul className="admin-sym-log">
                  {data.jobs.map((j) => (
                    <li key={j.id} className={cn("admin-sym-log__item", j.status === "error" && "isError")}>
                      <div className="admin-sym-log__top">
                        <AdminBadge variant={j.status === "error" ? "danger" : "success"}>{j.status}</AdminBadge>
                        <span className="admin-sym-log__type">{jobLabel(j.jobType)}</span>
                        <span className="admin-sym-log__time">{j.createdAt}</span>
                      </div>
                      <p className="admin-sym-log__msg">{j.message || "—"}</p>
                      {typeof j.meta?.durationMs === "number" || j.meta?.source || j.meta?.orderId != null || j.meta?.pendingLeft != null ? (
                        <p className="admin-sym-log__meta">
                          {j.meta.source ? `source=${String(j.meta.source)}` : null}
                          {typeof j.meta.durationMs === "number" ? ` · ${j.meta.durationMs}ms` : null}
                          {j.meta.orderId != null ? ` · order #${String(j.meta.orderId)}` : null}
                          {j.meta.pendingLeft != null ? ` · pending left ${String(j.meta.pendingLeft)}` : null}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="admin-sym-panel">
              <header className="admin-sym-panel__head">
                <h3>Order sync activity</h3>
                <span>Recent orders by Symphonya state</span>
              </header>
              {data.orders.length === 0 ? (
                <p className="admin-sym-panel__empty">No order sync activity found.</p>
              ) : (
                <ul className="admin-sym-log">
                  {data.orders.map((o) => (
                    <li key={o.id} className={cn("admin-sym-log__item", o.symphonyaLabel === "error" && "isError")}>
                      <div className="admin-sym-log__top">
                        <AdminBadge variant={symTone(o.symphonyaLabel)}>{o.symphonyaLabel}</AdminBadge>
                        <Link to="/vadmin/comenzi" className="admin-sym-log__type hover:underline">#{o.id} · {o.name || "Order"}</Link>
                        <span className="admin-sym-log__time">{o.dateLabel}</span>
                      </div>
                      {o.error ? <p className="admin-sym-log__msg">{o.error}</p> : (
                        <p className="admin-sym-log__msg">
                          _symphonya={o.symphonya}
                          {o.symphonyaLabel === "synced" ? " (ERP id)" : null}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      )}
    </AdminCard>
  );
}
